import {Component, OnInit, Renderer2} from '@angular/core';
import {CommonModule} from '@angular/common';
import {PRICING_DATA, PricingJsonItem} from './pricing-data';

interface PricingItem {
  category: string;
  moduleName: string;
  details: string;
  payAsYouGo: string;
}

@Component({
  selector: 'app-pricing',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss']
})
export class PricingComponent implements OnInit {
  pricingData: PricingItem[] = [];
  categories: string[] = [];
  activeCategory: string = ''; // Currently active category for filtered view
  // Contact dropdown state
  contactDropdownOpen: { [key: string]: boolean } = {};
  private readonly email = 'demo@mypart.com';

  constructor(private renderer: Renderer2) {
  }

  ngOnInit(): void {
    this.loadPricingData();
    // Setup click handler to close dropdowns when clicking outside
    this.setupOutsideClickHandler();
  }

  loadPricingData(): void {
    // Use local data instead of HTTP call
    this.pricingData = this.mapJsonToPricingItems(PRICING_DATA);
    this.initCategories();
  }

  getItemsByCategory(category: string): PricingItem[] {
    return this.pricingData.filter(item => item.category === category);
  }

  // Set the active category for filtered view
  setActiveCategory(category: string): void {
    this.activeCategory = category;
  }

  /**
   * Toggles the contact dropdown for a specific pricing tier
   */
  toggleContactDropdown(tier: string): void {
    // Close all other dropdowns first
    Object.keys(this.contactDropdownOpen).forEach(key => {
      if (key !== tier) {
        this.contactDropdownOpen[key] = false;
      }
    });

    // Toggle the selected dropdown
    this.contactDropdownOpen[tier] = !this.contactDropdownOpen[tier];
  }

  /**
   * Copies email to clipboard
   */
  async copyEmail(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.email);
      console.log('Email copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      this.fallbackCopyTextToClipboard(this.email);
    }
    this.closeAllDropdowns();
  }

  /**
   * Opens default email app
   */
  openEmailApp(): void {
    window.location.href = `mailto:${this.email}`;
    this.closeAllDropdowns();
  }

  /**
   * Closes all contact dropdowns
   */
  closeAllDropdowns(): void {
    Object.keys(this.contactDropdownOpen).forEach(key => {
      this.contactDropdownOpen[key] = false;
    });
  }

  /**
   * Opens the MyPart API access request page in a new tab
   */
  openApiAccess(): void {
    window.open('https://www.mypart.ai/api-access', '_blank');
  }

  /**
   * Fallback copy method for older browsers
   */
  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      console.log('Email copied to clipboard (fallback)');
    } catch (err) {
      console.error('Unable to copy email', err);
    }
    document.body.removeChild(textArea);
  }

  /**
   * Setup click handler to close dropdowns when clicking outside
   */
  private setupOutsideClickHandler(): void {
    this.renderer.listen('document', 'click', (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.contact-dropdown-container')) {
        this.closeAllDropdowns();
      }
    });
  }

  private mapJsonToPricingItems(jsonData: PricingJsonItem[]): PricingItem[] {
    return jsonData.map(item => ({
      category: item.Category || '',
      moduleName: item['Module Name'] || '',
      details: item.Details || '',
      payAsYouGo: this.formatPriceValue(item['Pay as you go'])
    }));
  }

  private formatPriceValue(value: number | string | undefined): string {
    if (value === undefined || value === null) return '';

    // If it's already a string with formatting, return as is
    if (typeof value === 'string') return value;

    // Format number as currency
    return `$${value.toFixed(2)}`;
  }

  private initCategories(): void {
    // Extract unique categories
    this.pricingData.forEach(item => {
      if (item.category && !this.categories.includes(item.category)) {
        this.categories.push(item.category);
      }
    });

    // Remove empty categories
    this.categories = this.categories.filter(cat => cat.trim() !== '');

    // Set the first category as active by default
    if (this.categories.length > 0) {
      this.activeCategory = this.categories[0];
    }
  }
}
